/**
 * logisticsStatus.js — single source of truth for classifying an FF&E item's
 * order/delivery status across every logistics view (Shipping & Tracking,
 * Delivery Scheduler, Critical Path).
 *
 * Design goals (from user feedback):
 *  - PULL ALL PIECES: include every item that has been committed to the order
 *    pipeline (APPROVED onward), not a narrow hardcoded whitelist. Only clearly
 *    not-yet-ordered / dead statuses are excluded.
 *  - SYNC WITH FF&E: classify purely off the canonical FF&E `item.status`
 *    field (the same field the FF&E spreadsheet writes), case-normalized.
 *  - KEEP ALL DELIVERY STATUSES: no collapsing. The full status set — including
 *    the several delivery destinations (DELIVERED TO RECEIVER / DELIVERED TO
 *    JOB SITE / RECEIVED) — is preserved and surfaced.
 *
 * The status list, phases and colors mirror the backend authoritative enum
 * ITEM_STATUSES (server.py ~line 377). Keep in sync if the backend changes.
 */

export const PHASE_ORDER = ['procurement', 'fulfillment', 'delivery', 'installation', 'exception'];

export const PHASE_LABELS = {
  planning: 'Planning',
  procurement: 'Procurement',
  fulfillment: 'Fulfillment',
  delivery: 'Delivery',
  installation: 'Installation',
  exception: 'Issues & Exceptions',
};

// Authoritative status metadata (value -> phase + color). Mirrors backend ITEM_STATUSES.
export const ITEM_STATUS_META = [
  { status: 'TO BE SELECTED', color: '#D4A574', phase: 'planning' },
  { status: 'RESEARCHING', color: '#B8860B', phase: 'planning' },
  { status: 'PENDING APPROVAL', color: '#DAA520', phase: 'planning' },
  { status: 'APPROVED', color: '#9ACD32', phase: 'procurement' },
  { status: 'ORDERED', color: '#32CD32', phase: 'procurement' },
  { status: 'PICKED', color: '#3B82F6', phase: 'procurement' },
  { status: 'CONFIRMED', color: '#228B22', phase: 'procurement' },
  { status: 'IN PRODUCTION', color: '#FF8C00', phase: 'fulfillment' },
  { status: 'SHIPPED', color: '#4169E1', phase: 'fulfillment' },
  { status: 'IN TRANSIT', color: '#6495ED', phase: 'fulfillment' },
  { status: 'OUT FOR DELIVERY', color: '#87CEEB', phase: 'fulfillment' },
  { status: 'DELIVERED TO RECEIVER', color: '#9370DB', phase: 'delivery' },
  { status: 'DELIVERED TO JOB SITE', color: '#8A2BE2', phase: 'delivery' },
  { status: 'RECEIVED', color: '#DDA0DD', phase: 'delivery' },
  { status: 'PARTIALLY DELIVERED', color: '#BA55D3', phase: 'delivery' },
  { status: 'READY FOR INSTALL', color: '#20B2AA', phase: 'installation' },
  { status: 'INSTALLING', color: '#48D1CC', phase: 'installation' },
  { status: 'INSTALLED', color: '#00CED1', phase: 'installation' },
  { status: 'ON HOLD', color: '#DC143C', phase: 'exception' },
  { status: 'BACKORDERED', color: '#B22222', phase: 'exception' },
  { status: 'DAMAGED', color: '#8B0000', phase: 'exception' },
  { status: 'RETURNED', color: '#CD5C5C', phase: 'exception' },
  { status: 'MISSING', color: '#A0522D', phase: 'exception' },
  { status: 'CHANGE OUT', color: '#FF6347', phase: 'exception' },
  { status: 'REPLACEMENT', color: '#FF7F50', phase: 'exception' },
  { status: 'CANCELLED', color: '#A52A2A', phase: 'exception' },
];

const META_BY_STATUS = ITEM_STATUS_META.reduce((m, s) => { m[s.status] = s; return m; }, {});

// Normalize a raw status string to the canonical UPPERCASE form.
export function normalizeStatus(status) {
  return (status || '').toString().trim().toUpperCase();
}

// Statuses that mean "not yet in the order pipeline" or dead — excluded from
// every logistics view. Everything else (APPROVED onward, incl. exceptions
// like ON HOLD / BACKORDERED) counts as an ordered piece so nothing is dropped.
const NOT_ORDERED = new Set([
  '', 'TO BE SELECTED', 'RESEARCHING', 'PENDING APPROVAL',
  'QUOTE REQUESTED', 'GET QUOTE', 'WAITING ON QT',
  'ORDER SAMPLES', 'SAMPLES ORDERED', 'SAMPLES ARRIVED',
  'ASK NEIL', 'ASK CHARLENE', 'ASK JALA', 'ASK AVERI',
  'READY FOR PRESENTATION', 'REJECTED', 'CANCELLED',
]);

// Statuses that mean the item has physically arrived (delivered/received/installed).
const ARRIVED = new Set([
  'DELIVERED TO RECEIVER', 'DELIVERED TO JOB SITE', 'RECEIVED',
  'READY FOR INSTALL', 'INSTALLING', 'INSTALLED',
]);

// Statuses actively moving toward the site (used for the "In Transit" rollup).
const IN_TRANSIT_SET = new Set(['SHIPPED', 'IN TRANSIT', 'OUT FOR DELIVERY']);

export function isOrdered(item) {
  return !NOT_ORDERED.has(normalizeStatus(item && item.status));
}

export function isArrived(item) {
  return ARRIVED.has(normalizeStatus(item && item.status));
}

export function isInTransit(item) {
  return IN_TRANSIT_SET.has(normalizeStatus(item && item.status));
}

// Ordered but not yet arrived.
export function isAwaitingDelivery(item) {
  return isOrdered(item) && !isArrived(item);
}

export function getPhase(status) {
  const meta = META_BY_STATUS[normalizeStatus(status)];
  return meta ? meta.phase : 'exception';
}

export function getStatusColor(status) {
  const meta = META_BY_STATUS[normalizeStatus(status)];
  return meta ? meta.color : '#9CA3AF';
}

/**
 * Where an item has (or will) land. The several delivery destinations are
 * carried today by the status value; the persisted `ship_to`/`delivery_location`
 * field (if present) takes precedence.
 */
export function getDeliveryLocation(item) {
  const explicit = (item && (item.ship_to || item.delivery_location || item.destination) || '').toString().trim();
  if (explicit) return explicit;
  const s = normalizeStatus(item && item.status);
  if (s === 'DELIVERED TO JOB SITE') return 'Job Site';
  if (s === 'DELIVERED TO RECEIVER' || s === 'RECEIVED') return 'Receiver / Warehouse';
  return 'Unassigned';
}

/**
 * Flatten a project's FF&E tree (rooms -> categories -> subcategories -> items),
 * attaching room_name and category_name to each item.
 */
export function flattenFFEItems(project) {
  const out = [];
  (project?.rooms || []).forEach((room) => {
    (room.categories || []).forEach((category) => {
      (category.subcategories || []).forEach((subcategory) => {
        (subcategory.items || []).forEach((item) => {
          out.push({ ...item, room_name: room.name, category_name: category.name });
        });
      });
    });
  });
  return out;
}

/**
 * Count ordered items by their exact (normalized) status. Returns { [status]: n }.
 * Every status that actually appears in the data is represented — nothing is
 * collapsed — so the caller can render one chip per real status.
 */
export function countByStatus(items) {
  const counts = {};
  (items || []).forEach((item) => {
    if (!isOrdered(item)) return;
    const s = normalizeStatus(item.status);
    counts[s] = (counts[s] || 0) + 1;
  });
  return counts;
}

/**
 * High-level rollup used for summary tiles that agree across all three views.
 */
export function summarize(items) {
  const ordered = (items || []).filter(isOrdered);
  return {
    total: ordered.length,
    inTransit: ordered.filter(isInTransit).length,
    arrived: ordered.filter(isArrived).length,
    awaiting: ordered.filter((i) => !isArrived(i)).length,
    byStatus: countByStatus(ordered),
  };
}
